import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

export type CleanupPriority = 'low' | 'normal' | 'high';

export interface CleanupTask {
  /** Unique name — used to prevent duplicate registration */
  name: string;
  /** The function to run during each cleanup cycle */
  fn: () => void | Promise<void>;
  /** Execution priority within the cycle */
  priority: CleanupPriority;
}

export interface CleanupCycleResult {
  ranAt: Date;
  tasksRun: number;
  errors: string[];
  durationMs: number;
}

const PRIORITY_ORDER: Record<CleanupPriority, number> = {
  high: 0,
  normal: 1,
  low: 2,
};

/**
 * AutoCleanupService — Runs registered cleanup tasks on a configurable schedule.
 *
 * Usage:
 *   autoCleanup.register({ name: 'cache-flush', priority: 'high', fn: () => cache.flush() });
 *   autoCleanup.register({ name: 'weak-ref-gc', priority: 'normal', fn: () => registry.cleanup() });
 *
 * Default interval: 30 minutes
 * Tasks are run in priority order: high → normal → low
 */
@Injectable({ providedIn: 'root' })
export class AutoCleanupService implements OnDestroy {
  /** Emits after each cleanup cycle completes */
  readonly cycleComplete$ = new Subject<CleanupCycleResult>();

  private readonly tasks: CleanupTask[] = [];
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isRunning = false;

  // 30 minutes between automatic cleanup cycles
  private readonly DEFAULT_INTERVAL_MS = 30 * 60 * 1000;

  constructor(private readonly ngZone: NgZone) {}

  /**
   * Start the automatic cleanup scheduler.
   * @param intervalMs Milliseconds between cycles (default: 30 minutes)
   */
  start(intervalMs: number = this.DEFAULT_INTERVAL_MS): void {
    if (this.intervalId !== null) {
      console.warn('[AutoCleanup] Already running — call stop() before restarting');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.runCycle(), intervalMs);
    });

    console.log(`[AutoCleanup] Scheduler started — interval: ${intervalMs / 60_000}min`);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[AutoCleanup] Scheduler stopped');
    }
  }

  /**
   * Register a cleanup task.
   * If a task with the same name already exists, it will NOT be replaced
   * (call unregister first if you need to update it).
   */
  register(task: CleanupTask): void {
    if (this.tasks.find(t => t.name === task.name)) {
      console.warn(`[AutoCleanup] Task "${task.name}" already registered — skipping`);
      return;
    }
    this.tasks.push(task);
    console.log(`[AutoCleanup] Registered task: "${task.name}" (priority: ${task.priority})`);
  }

  /** Unregister a task by name */
  unregister(name: string): void {
    const idx = this.tasks.findIndex(t => t.name === name);
    if (idx !== -1) {
      this.tasks.splice(idx, 1);
      console.log(`[AutoCleanup] Unregistered task: "${name}"`);
    }
  }

  /** Manually trigger a cleanup cycle immediately */
  async runNow(): Promise<CleanupCycleResult> {
    return this.runCycle();
  }

  private async runCycle(): Promise<CleanupCycleResult> {
    if (this.isRunning) {
      console.warn('[AutoCleanup] Cycle already in progress — skipping');
      return { ranAt: new Date(), tasksRun: 0, errors: ['Cycle in progress'], durationMs: 0 };
    }

    this.isRunning = true;
    const startTime = Date.now();
    const errors: string[] = [];
    let tasksRun = 0;

    console.log(`[AutoCleanup] Cycle started at ${new Date().toISOString()}`);

    const sorted = [...this.tasks].sort(
      (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
    );

    for (const task of sorted) {
      try {
        await task.fn();
        tasksRun++;
        console.log(`[AutoCleanup]   ✔ ${task.name}`);
      } catch (err) {
        const msg = `Task "${task.name}" failed: ${String(err)}`;
        errors.push(msg);
        console.error(`[AutoCleanup]   ✖ ${msg}`);
      }
    }

    const result: CleanupCycleResult = {
      ranAt: new Date(),
      tasksRun,
      errors,
      durationMs: Date.now() - startTime,
    };

    this.isRunning = false;

    this.ngZone.run(() => this.cycleComplete$.next(result));

    console.log(
      `[AutoCleanup] Cycle complete — ${tasksRun} tasks, ` +
      `${errors.length} errors, ${result.durationMs}ms`
    );

    return result;
  }

  ngOnDestroy(): void {
    this.stop();
    this.cycleComplete$.complete();
  }
}
